import SparkMD5 from 'spark-md5';

/**
 * 官网接口请求封装。
 * 与小程序（miniProgram/utils/util.js）保持一致的签名算法：
 *   sign = MD5( "SIGNKEY={key};TIMESTAMP={ts};METHODNAME={method};" [+ "TOKEN=Bearer {token};"] )
 * 请求通过 POST 发送 JSON，响应统一为 { code, message, data }。
 */

export interface ApiResponse<T = unknown> {
  code: number;
  message?: string;
  data: T;
  time?: string;
}

/** 分页列表响应（对应后端 SqlSugarPagedList） */
export interface PagedList<T> {
  pageIndex: number;
  pageSize: number;
  total: number;
  pages: number;
  list: T[];
  hasPrevPages?: boolean;
  hasNextPages?: boolean;
}

/** 单对象响应（对应后端 BaseInfoResponse） */
export interface DataInfoResponse<T> {
  dataInfo: T;
}

const API_BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) || 'https://api.yuanhe.gzdaone.com/api';

const API_SIGN_KEY: string =
  (import.meta.env.VITE_API_SIGN_KEY as string | undefined) || 'yh@lWqNRaRlWXYUsHlX';

/**
 * 资源文件基础地址。
 * 后端 ImgFull 逻辑为「当前接口域名 + 相对路径」，此处从接口地址推导同源前缀；
 * 开发环境接口为 vite 代理相对路径（/api），回退到后端域名。
 */
export const ASSET_BASE_URL: string = (() => {
  if (API_BASE_URL.startsWith('http')) {
    try {
      return new URL(API_BASE_URL).origin;
    } catch {
      /* ignore */
    }
  }
  return 'https://api.yuanhe.gzdaone.com';
})();

const ASSET_HOST: string = (() => {
  try {
    return new URL(ASSET_BASE_URL).host;
  } catch {
    return '';
  }
})();

/**
 * 拼接资源完整地址（http/https 开头原样返回；用于处理各图片字段）。
 * 后端 ImgFull 固定返回 http:// 全路径，官网以 https 部署时需将接口域名的图片升级为 https，
 * 避免浏览器按混合内容（Mixed Content）拦截。
 */
export function resolveAssetUrl(url?: string | null): string {
  const raw = (url ?? '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) {
    if (
      raw.startsWith('http://') &&
      ASSET_HOST &&
      typeof window !== 'undefined' &&
      window.location.protocol === 'https:'
    ) {
      try {
        const parsed = new URL(raw);
        if (parsed.host === ASSET_HOST) {
          return `https://${parsed.host}${parsed.pathname}${parsed.search}`;
        }
      } catch {
        /* ignore */
      }
    }
    return raw;
  }
  return `${ASSET_BASE_URL}${raw.startsWith('/') ? '' : '/'}${raw}`;
}

/**
 * 处理富文本 HTML 中的图片地址（对齐 resolveAssetUrl 的混合内容策略）。
 * 管理端上传接口返回的是后端 http 完整地址，官网 https 部署时需升级为 https；
 * data:/blob: 等内联地址原样保留。
 */
export function resolveHtmlAssetUrls(html?: string | null): string {
  const raw = (html ?? '').trim();
  if (!raw) return '';
  return raw.replace(/src="([^"]*)"/gi, (match, url: string) => {
    const trimmed = String(url).trim();
    if (!trimmed || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) return match;
    const resolved = resolveAssetUrl(trimmed);
    return resolved ? `src="${resolved}"` : match;
  });
}

const TOKEN_STORAGE_KEY = 'portal_token';
const USER_STORAGE_KEY = 'portal_user';

export interface PortalUser {
  token: string;
  uId: number;
  userName: string;
  /** 登录手机号（前端登录时记录，用于界面展示） */
  mobile?: string;
}

export function getToken(): string {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

export function getPortalUser(): PortalUser | null {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PortalUser) : null;
  } catch {
    return null;
  }
}

export function setPortalUser(user: PortalUser): void {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, user.token);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  } catch {
    /* ignore */
  }
}

export function clearPortalUser(): void {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** 登录失效事件订阅（401/403 时触发，用于打开登录弹窗） */
type UnauthorizedHandler = (message: string) => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

export function onUnauthorized(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

export class ApiError extends Error {
  code: number;

  constructor(code: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
  }
}

/** 生成签名请求头 */
function buildHeaders(methodName: string, timeStamp: number): Record<string, string> {
  let sign = `SIGNKEY=${API_SIGN_KEY};TIMESTAMP=${timeStamp};METHODNAME=${methodName};`;
  const token = getToken();

  const headers: Record<string, string> = {
    'content-type': 'application/json',
    TimeStamp: String(timeStamp),
    MethodName: methodName,
  };

  if (token) {
    sign += `TOKEN=Bearer ${token};`;
    headers.Authorization = `Bearer ${token}`;
  }

  headers.Sign = SparkMD5.hash(sign).toUpperCase();
  return headers;
}

/**
 * 发起接口请求。
 * @param methodName 接口方法名，如 "/course/userGetCourseList"
 * @param data 请求体
 */
export async function request<T>(methodName: string, data: Record<string, unknown> | object = {}): Promise<T> {
  const timeStamp = Math.floor(Date.now() / 1000);
  const headers = buildHeaders(methodName, timeStamp);

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${methodName}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
  } catch {
    throw new ApiError(-1, '网络连接失败，请检查网络后重试');
  }

  let body: ApiResponse<T> | null = null;
  try {
    body = (await response.json()) as ApiResponse<T>;
  } catch {
    throw new ApiError(response.status || -1, '服务响应异常，请稍后重试');
  }

  if (!body || typeof body.code !== 'number') {
    throw new ApiError(response.status || -1, '服务响应异常，请稍后重试');
  }

  if (body.code === 200) {
    return body.data;
  }

  const message = typeof body.message === 'string' && body.message ? body.message : '请求失败，请稍后重试';

  if (body.code === 401 || body.code === 403) {
    clearPortalUser();
    unauthorizedHandler?.(message);
    throw new ApiError(body.code, message);
  }

  throw new ApiError(body.code, message);
}
